import { useEffect, useState, useRef, useCallback } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Tv,
  Volume2,
  VolumeX,
} from "lucide-react";
import { getNewsAnchorVideos } from "../../../../api/home";

/* ═══════════════════════════════════════════════════════════════
   NewsAnchorWidget — video carousel for AI news reports
   ═══════════════════════════════════════════════════════════════ */
const NewsAnchorWidget = ({ dark, t, onEmpty }) => {
  const [videos, setVideos] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hovered, setHovered] = useState(false);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  /* ── Fetch video list ── */
  useEffect(() => {
    setLoading(true);
    setError(null);
    getNewsAnchorVideos()
      .then((res) => {
        const data = res?.data || [];
        const list = typeof data === "string" ? JSON.parse(data) : data;
        setVideos(list);
        if (list.length === 0) onEmpty?.();
      })
      .catch((e) => setError(e?.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  /* ── Reset video element when index changes ── */
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [activeIdx]);

  const goPrev = useCallback(
    (e) => {
      e.stopPropagation();
      setActiveIdx((i) => (i <= 0 ? videos.length - 1 : i - 1));
    },
    [videos.length],
  );

  const goNext = useCallback(
    (e) => {
      e.stopPropagation();
      setActiveIdx((i) => (i >= videos.length - 1 ? 0 : i + 1));
    },
    [videos.length],
  );

  const toggleMute = useCallback((e) => {
    e.stopPropagation();
    setMuted((m) => !m);
  }, []);

  const current = videos[activeIdx];

  /* ── Loading / Error / Empty ── */
  if (loading) {
    return (
      <div
        className="h-full w-full flex items-center justify-center"
        style={{ color: t.textMuted }}
      >
        <Loader2
          size={16}
          className="animate-spin"
          style={{ color: t.activeText }}
        />
      </div>
    );
  }

  if (error || !current) {
    return (
      <div
        className="h-full w-full flex flex-col items-center justify-center gap-1.5"
        style={{ color: t.textMuted }}
      >
        <Tv size={18} style={{ opacity: 0.3 }} />
        <span className="text-[10px]">{error || "No reports"}</span>
      </div>
    );
  }

  const borderColor = dark ? "rgba(14,165,233,0.15)" : "rgba(203,213,225,0.3)";

  return (
    <div
      className="flex flex-col h-full w-full"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 py-1.5 shrink-0"
        style={{ borderBottom: `1px solid ${t.cardBorder}` }}
      >
        <div className="flex items-center gap-1.5">
          <Tv
            className="h-3 w-3 shrink-0"
            style={{ color: t.textSecondary }}
            strokeWidth={2}
          />
          <span
            className="text-[11px] font-medium"
            style={{ color: t.textPrimary }}
          >
            News Reports
          </span>
        </div>
        {videos.length > 1 && (
          <span
            className="text-[9px] tabular-nums"
            style={{ color: t.textMuted }}
          >
            {activeIdx + 1} / {videos.length}
          </span>
        )}
      </div>

      {/* Video area */}
      <div className="flex-1 relative overflow-hidden" style={{ minHeight: 0 }}>
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          src={current.video}
          muted={muted}
          autoPlay
          playsInline
          onEnded={() => {
            if (videos.length > 1) {
              setActiveIdx((i) => (i >= videos.length - 1 ? 0 : i + 1));
            }
          }}
          style={{ background: "#000" }}
        />

        {/* Title overlay at bottom */}
        {current.title && (
          <div
            className="absolute bottom-0 left-0 right-0 px-3 py-2"
            style={{
              background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
            }}
          >
            <p className="text-[10px] leading-snug text-white/90 line-clamp-2">
              {current.title.split("\n")[0]}
            </p>
            {current.author && (
              <p className="text-[9px] text-white/50 mt-0.5">
                {current.author}
              </p>
            )}
          </div>
        )}

        {/* Prev / Next buttons — show on hover */}
        {videos.length > 1 && (
          <>
            <button
              onClick={goPrev}
              className="absolute left-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-150"
              style={{
                background: "rgba(0,0,0,0.5)",
                border: `1px solid ${borderColor}`,
                opacity: hovered ? 1 : 0,
                pointerEvents: hovered ? "auto" : "none",
                backdropFilter: "blur(4px)",
              }}
            >
              <ChevronLeft
                size={14}
                style={{ color: "rgba(255,255,255,0.85)" }}
              />
            </button>
            <button
              onClick={goNext}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-150"
              style={{
                background: "rgba(0,0,0,0.5)",
                border: `1px solid ${borderColor}`,
                opacity: hovered ? 1 : 0,
                pointerEvents: hovered ? "auto" : "none",
                backdropFilter: "blur(4px)",
              }}
            >
              <ChevronRight
                size={14}
                style={{ color: "rgba(255,255,255,0.85)" }}
              />
            </button>
          </>
        )}

        {/* Mute toggle — top right on hover */}
        <button
          onClick={toggleMute}
          className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-150"
          style={{
            background: "rgba(0,0,0,0.5)",
            border: `1px solid ${borderColor}`,
            opacity: hovered ? 1 : 0,
            pointerEvents: hovered ? "auto" : "none",
            backdropFilter: "blur(4px)",
          }}
        >
          {muted ? (
            <VolumeX size={11} style={{ color: "rgba(255,255,255,0.85)" }} />
          ) : (
            <Volume2 size={11} style={{ color: "rgba(255,255,255,0.85)" }} />
          )}
        </button>

        {/* Dot indicators */}
        {videos.length > 1 && (
          <div
            className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-1"
            style={{
              opacity: hovered ? 1 : 0,
              transition: "opacity 0.15s",
            }}
          >
            {videos.map((_, i) => (
              <button
                key={i}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIdx(i);
                }}
                className="rounded-full transition-all duration-150"
                style={{
                  width: i === activeIdx ? 12 : 5,
                  height: 5,
                  background:
                    i === activeIdx
                      ? "rgba(14,165,233,0.9)"
                      : "rgba(255,255,255,0.4)",
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsAnchorWidget;
